/**
 * Dashboard Error Boundary Component
 * Catches React errors and displays fallback UI while reporting to Sentry
 */

import { Sentry } from '@charitystack/shared';
import { Result, Button } from 'antd';

/**
 * Fallback UI component displayed when an error is caught
 */
const ErrorFallback = () => (
  <Result
    status="error"
    title="Something went wrong"
    subTitle="We've been notified and are looking into it. Please try reloading the dashboard."
    extra={
      // A real page reload, not Sentry's resetError: resetError only re-renders
      // the same (possibly stale) bundle, which cannot recover from a missing
      // lazy-route chunk after a deploy — the same import fails again.
      <Button type="primary" onClick={() => window.location.reload()}>
        Reload Dashboard
      </Button>
    }
  />
);

/**
 * Dashboard Error Boundary
 * Wraps the entire dashboard application to catch unhandled React errors
 *
 * Features:
 * - Displays user-friendly fallback UI
 * - Reports errors to Sentry with dashboard context
 * - Sets error level and boundary tag for filtering
 * - Disables user feedback dialog (showDialog: false)
 */
export const DashboardErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={ErrorFallback}
    showDialog={false}
    beforeCapture={(scope) => {
      scope.setTag('boundary', 'dashboard_root');
      scope.setLevel('error');
    }}
  >
    {children}
  </Sentry.ErrorBoundary>
);
